import { AccordionItem, Link } from '@mm/components';
import { AttributeIcon } from '@mm/icons';
import AttributeCRUDActions, {
  useAttributeCRUDActions,
} from '@mm/state/actions/AttributeCRUDActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { identifyChannel } from '@mm/state/helpers/live-chat-helpers';
import { ContactVariable } from '@mm/state/interfaces/contacts';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import { camelCase, isNil } from 'lodash';
import moment from 'moment';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import validator from 'validator';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PrimaryToolTip } from '../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { SearchInput } from '../../../../../../../common/components/SearchInput/SearchInput';
import { ThreeDotMenu } from '../../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import './chatVariablesTab.pcss';

type OuterProps = {
  isLiveChatContacts?: boolean;
  variables: ContactVariable[];
  contactId?: number;
  onAttributeUpdated?: () => void;
  remoteUserId: string;
  collapsedContactView?: boolean;
  withHeaderButton?: boolean;
  withHeaderButtonText?: string;
  onHeaderButtonClick?: any;
  openArrowClassName?: string;
  headerButtonCss?: {
    container?: string;
    leftIcon?: string;
  };
};

const EDITABLE_SYSTEM_VARS = ['first name', 'last name', 'full name'];

type InnerProps = ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const processAttributes = (attr: any): ReactNode => {
  const time = moment.utc(attr, 'YYYY-MM-DD HH:mm:ss UTC', true);
  switch (true) {
    case typeof attr !== 'string':
      return <>{String(attr)}</>;

    case validator.isMobilePhone(attr):
      return (
        <Link href={`tel:${attr}`} capitalize={false} isBlank>
          {attr}
        </Link>
      );

    case validator.isEmail(attr):
      return (
        <Link href={`mailto:${attr}`} capitalize={false} isBlank>
          {attr}
        </Link>
      );

    case validator.isURL(attr):
      return (
        <Link href={attr} capitalize={false} isBlank>
          {attr}
        </Link>
      );

    case time.isValid():
      return (
        <PrimaryToolTip
          align="center"
          position="top"
          anchorElement={<span>{time.local().fromNow()}</span>}
          classNameContainer="live-chat--contact-attributes__tooltip"
          classNameContent="live-chat--contact-attributes-tooltip__content"
          useHover
        >
          {time.format('YYYY-MM-DD HH:mm:ss')}
        </PrimaryToolTip>
      );

    default:
      return <>{attr}</>;
  }
};

const ChatVariablesTab = (props: Props) => {
  const {
    variables,
    isLiveChatContacts = false,
    onAttributeUpdated,
    onAttributeAdd,
    contactId,
    remoteUserId,
    collapsedContactView,
    withHeaderButton,
    withHeaderButtonText,
    onHeaderButtonClick,
    openArrowClassName,
    headerButtonCss,
  } = props;
  const [filteredVariables, setFilteredVariables] = useState(variables);
  const [searchText, setSearchText] = useState('');
  const [newAttributeNameText, setNewAttributeNameText] = useState('');
  const [newAttributeValueText, setNewAttributeValueText] = useState('');
  const [editAttributeName, setEditAttributeName] = useState('');

  const { update: updateContact } = useContactsCRUDActions();
  const { deleteAttributeValue } = useAttributeCRUDActions();

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const channel = identifyChannel(remoteUserId);
  const allowSystemVariableEditing = channel !== 'facebook';
  const { openGeneralConfirmModal } = useModalActions();

  useEffect(() => {
    updateFilteredVariables(searchText);
  }, [variables]);

  useEffect(() => {
    setDefaults();
    setSearchText('');
    updateFilteredVariables('');
  }, [contactId]);

  const updateFilteredVariables = (newSearchText: string) => {
    if (!newSearchText) {
      setFilteredVariables(variables);
      return;
    }
    const lowerSearchText = newSearchText.toLowerCase();
    setFilteredVariables(
      variables.filter(
        v => v.variable.name.toLowerCase().indexOf(lowerSearchText) >= 0,
      ),
    );
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText =
      e.target.value.length === 1 ? e.target.value.trim() : e.target.value;
    setSearchText(newSearchText);
    updateFilteredVariables(newSearchText);
  };

  const addAttribute = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const attributeNameText = newAttributeNameText.trim();
    const attributeValueText = newAttributeValueText.trim();
    if (attributeNameText && attributeValueText) {
      if (EDITABLE_SYSTEM_VARS.includes(editAttributeName)) {
        if (contactId) {
          await updateContact(facebookPageId, {
            id: contactId,
            [camelCase(editAttributeName)]: attributeValueText,
          });
        }
        onAttributeUpdated?.();
        setDefaults();
        return;
      }
      const newAttribute = await onAttributeAdd(
        attributeNameText,
        attributeValueText,
      );
      if (newAttribute) {
        onAttributeUpdated?.();
        setDefaults();
      }
    }
  };

  const handleSetNewAttributeNameText = (e: ChangeEvent<HTMLInputElement>) =>
    setNewAttributeNameText(e.target.value);
  const handleSetNewAttributeValueText = (e: ChangeEvent<HTMLInputElement>) =>
    setNewAttributeValueText(e.target.value);

  const onClickEdit = (attribute: ContactVariable) => {
    setEditAttributeName(attribute.variable.name);
    setNewAttributeValueText(attribute.value);
    setNewAttributeNameText(attribute.variable.name);
  };

  const setDefaults = () => {
    setEditAttributeName('');
    setNewAttributeNameText('');
    setNewAttributeValueText('');
  };

  const cancelAttributeEditing = () => {
    setDefaults();
  };

  const attributesWrapperClasses = classnames('contact-attributes__container', {
    'live-chat--contact-attributes__container': isLiveChatContacts,
  });

  const onRemoveAttribute = (item: ContactVariable) => {
    openGeneralConfirmModal({
      headerText: 'Remove attribute',
      messageText: `Are you sure you want to remove this attribute from this contact?`,
      okButtonText: 'Remove',
      cancelButtonFirst: true,
      onCloseHandler: async ok => {
        if (ok) {
          await deleteAttributeValue(item.id);
          onAttributeUpdated?.();
        }
      },
    });
  };

  const showActions = (item: ContactVariable) => {
    const edit = { name: 'Edit', value: 'edit' };
    if (item.variable.name === 'UNSUBSCRIBED') {
      return [{ name: 'Set to true', value: 'setTrue' }];
    }
    if (EDITABLE_SYSTEM_VARS.includes(item.variable.name)) {
      return [edit];
    }
    return [
      edit,
      {
        name: 'Remove',
        value: 'remove',
      },
    ];
  };

  const setUnsubscribeTrue = async () => {
    if (contactId) {
      await onAttributeAdd('UNSUBSCRIBED', 'true');
      onAttributeUpdated?.();
    }
  };

  const onSelectItem = (item: ContactVariable, action: string) => {
    // Note: not generalizing for now, since it is one time only thing
    if (action === 'setTrue') {
      setUnsubscribeTrue();
    }
    if (action === 'edit') {
      onClickEdit(item);
    }
    if (action === 'remove') {
      onRemoveAttribute(item);
    }
  };

  if (collapsedContactView) {
    return (
      <div className="live-chat--contact__header--collapsed">
        <AttributeIcon className="live-chat--contact__header-icon" />
        <span className="live-chat--contact__content-number--collapsed">
          ({filteredVariables.length})
        </span>
      </div>
    );
  }

  return (
    <div className={attributesWrapperClasses}>
      <AccordionItem
        icon={<AttributeIcon className="live-chat--contact__header-icon" />}
        title="Attributes"
        count={filteredVariables.length}
        closeOnOutsideClick={false}
        openOnMount
        withHeaderButton={withHeaderButton}
        withHeaderButtonText={withHeaderButtonText}
        headerButtonCss={
          headerButtonCss || {
            container: 'contact-attributes__header-button',
          }
        }
        openArrowClassName={openArrowClassName}
        onHeaderButtonClick={onHeaderButtonClick}
      >
        <div className="contact-attributes__main-content">
          <div className="contact-attributes__header">Add Attribute</div>
          <form onSubmit={addAttribute}>
            <div className="live-chat--contact-attributes-input-container">
              <input
                value={newAttributeNameText}
                placeholder="Name"
                onChange={handleSetNewAttributeNameText}
              />
              <input
                value={newAttributeValueText}
                placeholder="Value"
                onChange={handleSetNewAttributeValueText}
              />
              <PrimaryButton
                className="live-chat--contact-attributes-add-btn"
                disabled={
                  newAttributeNameText.trim().length === 0 ||
                  newAttributeValueText.trim().length === 0
                }
                type="submit"
              >
                Add
              </PrimaryButton>
            </div>
          </form>

          <SearchInput
            placeholder="Search attribute"
            className="contact-attributes__search"
            value={searchText}
            onChange={onChange}
          />

          <div className="contact-attributes__attributes">
            {filteredVariables.map((variable: ContactVariable, idx) => {
              const unsbuscribedEditable =
                variable.variable.name === 'UNSUBSCRIBED'
                  ? channel === 'sms' && variable.value === 'false'
                  : true;

              const notEditable =
                !(
                  allowSystemVariableEditing &&
                  EDITABLE_SYSTEM_VARS.includes(variable.variable.name)
                ) &&
                (variable.variable.system ||
                  !variable.id ||
                  !unsbuscribedEditable);
              return !notEditable &&
                editAttributeName === variable.variable.name ? (
                <form onSubmit={addAttribute} key={`${variable.id}-${idx}`}>
                  <div className="contact-attributes__editable-attribute-row">
                    <div className="live-chat--contact-attributes-input-container">
                      <input
                        value={newAttributeNameText}
                        placeholder="Name"
                        onChange={handleSetNewAttributeNameText}
                        disabled
                      />
                      <input
                        value={newAttributeValueText}
                        placeholder="Value"
                        onChange={handleSetNewAttributeValueText}
                      />
                      <div className="live-chat--contact-attributes-buttons live-chat--contact-attributes-buttons_edit">
                        <PrimaryButton
                          className="live-chat--contact-attributes-add-btn"
                          disabled={
                            newAttributeNameText.trim().length === 0 ||
                            newAttributeValueText.trim().length === 0
                          }
                          type="submit"
                        >
                          Save
                        </PrimaryButton>
                        <PrimaryButton
                          className="live-chat--contact-attributes-cancel-btn"
                          onClick={cancelAttributeEditing}
                          color={ButtonColor.invertedRed}
                        >
                          Cancel
                        </PrimaryButton>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                !isNil(variable.value) && (
                  <div
                    key={variable.id}
                    className="contact-attributes__attribute-row"
                  >
                    <div className="contact-attributes__attribute-name">
                      {variable.variable.name}
                    </div>
                    <div className="contact-attributes__attribute-value">
                      {processAttributes(variable.value)}
                    </div>
                    {!notEditable && (
                      <ThreeDotMenu
                        elements={showActions(variable)}
                        selectItem={action => onSelectItem(variable, action)}
                      />
                    )}
                  </div>
                )
              );
            })}
          </div>
        </div>
      </AccordionItem>
    </div>
  );
};

const mapDispatchToProps = (
  dispatch: Dispatch<StoreState>,
  outerProps: OuterProps,
) => ({
  onAttributeAdd: (attributeName: string, attributeValue: string) => {
    if (outerProps.contactId) {
      return dispatch(
        AttributeCRUDActions.addAttribute(
          outerProps.contactId,
          attributeName,
          attributeValue,
        ),
      );
    }
  },
});

const enhance = compose<InnerProps, OuterProps>(
  connect(null, mapDispatchToProps),
);

export default enhance(ChatVariablesTab);
